import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getFlags, createFlag, updateFlag, deleteFlag } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetFlags = (module, options = {}) => {
  const { enabled = true, page, pageSize, search } = options || {};
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["flags", module, page, pageSize, search],
    queryFn: () => getFlags(module, { page, pageSize, search }),
    enabled,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useCreateFlag = ({onClose} = {}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (data) => createFlag(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["flags"] });
      
      showNotification('success', 'Flag created successfully!');
      
      if(onClose) {
        onClose();
      } else{
        navigate('/flags');
      }

    },
    onError: (error) => {
      console.error("Error creating flag:", error);
      
      // Show error notification
      let errorMessage = 'Error creating flag. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};


export const useUpdateFlag = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: ({ id, data }) => updateFlag(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["flags"] });
      
      // Show success notification
      showNotification('success', 'Flag updated successfully!');
      
      // Navigate immediately without setTimeout
      navigate('/flags');
    },
    onError: (error) => {
      console.error("Error updating flag:", error);
      
      // Show error notification
      let errorMessage = 'Error updating flag. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteFlag = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (id) => deleteFlag(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["flags"] });
      
      // Show success notification
      showNotification('success', 'Flag deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting flag:", error);
      
      // Show error notification
      let errorMessage = 'Error deleting flag. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
}; 